import React, { FC, ChangeEvent } from 'react';
import styled, { css } from 'styled-components';
import Countries from './Countries';
import { Icon } from '../Icon';
import { CountryFlag } from './CountryFlag';
import { Input } from '../Input';
import { FormElement } from '../Form';
import { CosmosComponent } from 'src/types';

const getCountryNameCodes = (countryCode): string | string[] => {
  const countries = Object.keys(Countries).filter(
    (code) => Countries[code].phoneCode === countryCode,
  );

  if (countries?.length === 2) {
    return countries;
  } else if (countries?.length >= 1) {
    return countries[0];
  }

  return '';
};

export interface Phone {
  countryCode: string;
  phoneNumber?: string;
}

interface PhoneInputProps extends FormElement<CosmosComponent> {
  placeholder?: string;
  value: Phone;
  onChange: (value: Phone) => void;
  isError?: boolean;
  isInverted?: boolean;
}

const defaultProps: Partial<PhoneInputProps> = {
  isInverted: false,
  isError: false,
};

const PhoneInput: FC<PhoneInputProps> = ({
  placeholder,
  isInverted,
  value,
  isError,
  onChange,
}) => {
  const handleCountryChange = (event: ChangeEvent<HTMLSelectElement>) => {
    onChange({ ...value, countryCode: event.target.value });
  };

  const handleInputChange = (inputValue: string) => {
    const newValue = inputValue.trim();
    if (newValue === '' || !isNaN(newValue as any)) {
      onChange({ ...value, phoneNumber: newValue });
    }
  };

  return (
    <PhoneInputWrapper>
      <CountryWrapper>
        <CountriesSelect
          value={value.countryCode}
          onChange={handleCountryChange}
        >
          {Object.keys(Countries).map((code, index) => (
            <Option key={`${code}-${index}`} value={Countries[code].phoneCode}>
              {`${Countries[code].name} (${Countries[code].phoneCode})`}
            </Option>
          ))}
        </CountriesSelect>
        <CountryFlagWithCode isInverted={isInverted} isError={isError}>
          <CountryFlag
            countryCode={getCountryNameCodes(value.countryCode)}
            size="small"
          />
          <CountryCode>{`+${value.countryCode}`}</CountryCode>
          <ArrowDownIcon
            name="chevron"
            type="chevron"
            size="small2"
            isInverted={isInverted}
          />
        </CountryFlagWithCode>
      </CountryWrapper>
      <StyledPhoneInput
        type="tel"
        value={value.phoneNumber}
        onInputChange={handleInputChange}
        placeholder={placeholder}
        isInverted={isInverted}
        isError={isError}
      ></StyledPhoneInput>
    </PhoneInputWrapper>
  );
};

const PhoneInputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  height: 2.5em;
  ${({ theme }) => theme?.overrides?.PhoneInput};
`;

const CountryWrapper = styled.div`
  position: relative;
  height: 100%;
  flex: 1;
  flex-basis: 33.33%;
  ${({ theme }) => theme?.overrides?.PhoneInput?.CountryWrapper};
`;

const CountriesSelect = styled.select`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  z-index: 1;
  border: 0;
  opacity: 0;
  cursor: pointer;
`;

const Option = styled.option``;

type CountryFlagWithCodeProps = {
  isInverted?: boolean;
  isError?: boolean;
};
const CountryFlagWithCode = styled.div<CountryFlagWithCodeProps>`
  display: flex;
  align-items: center;
  border: 1px solid inherit;
  border-radius: 2px;
  height: 100%;
  width: 100%;
  box-sizing: border-box;
  padding: 0.5em 1em;
  color: ${({ theme: { colors } }) => colors?.textColor};
  background-color: ${({ theme: { colors } }) => colors?.white};
  border: 1px solid ${({ theme: { colors } }) => colors?.borderGray};
  border-radius: 2px;
  padding: 0 0.5em;

  ${({ theme }) => theme?.overrides?.PhoneInput?.CountryDropDown}

  ${({ isInverted }) =>
    isInverted &&
    css`
      background-color: ${({ theme: { colors } }) => colors?.invertedDefault};
      color: ${({ theme: { colors } }) => colors?.white};
      border: none;

      &:hover {
        background-color: ${({ theme: { colors } }) => colors?.battleshipGrey};
      }
    `};
  ${({ isError }) =>
    isError &&
    css`
      border: 1px solid ${({ theme: { colors } }) => colors?.red};
    `};
`;

const CountryCode = styled.span`
  font-size: 14px;
  margin-left: 0.5em;
`;

const ArrowDownIcon = styled(Icon)`
  margin-left: auto;
`;

const StyledPhoneInput = styled(Input)`
  flex: 1;
  flex-basis: 33.33%;
  margin-left: 0.5em;

  ${({ theme }) => theme?.overrides?.PhoneInput?.Input}
`;

PhoneInput.defaultProps = defaultProps;
export { PhoneInput };
